import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    GET_TERMS_OF_SERVICE,
    CREATE_TERMS_OF_SERVICE,
    UPDATE_TERMS_OF_SERVICE,
    ADD_TERMS_SECTION,
    UPDATE_TERMS_SECTION,
    DELETE_TERMS_SECTION,
    ADD_TERMS_SUB_SECTION,
    UPDATE_TERMS_SUB_SECTION,
    DELETE_TERMS_SUB_SECTION,
} from '@/services/ApiRoutes';
import { get, post, put, del } from '@/services/ApiService';

export interface SubSection {
    id?: string;
    _id?: string;
    title: string;
    content: string;
}

export interface Section {
    id?: string;
    _id?: string;
    title: string;
    order: number;
    subSections: SubSection[];
}

export interface TermsOfService {
    id?: string;
    _id?: string;
    lastUpdated: string;
    tableOfContents: string[];
    sections: Section[];
    createdAt?: string;
    updatedAt?: string;
}

interface TermsState {
    data: TermsOfService | null;
    loading: boolean;
    error: string | null;
    sectionLoading: boolean;
}

const initialState: TermsState = {
    data: null,
    loading: false,
    error: null,
    sectionLoading: false,
};

// Async Thunks
export const fetchTermsOfService = createAsyncThunk(
    'terms/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_TERMS_OF_SERVICE);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch terms of service');
        }
    }
);

export const createTermsOfService = createAsyncThunk(
    'terms/create',
    async (data: { lastUpdated: string; sections: Section[] }, { rejectWithValue }) => {
        try {
            const response = await post(CREATE_TERMS_OF_SERVICE, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create terms of service');
        }
    }
);

export const updateTermsOfService = createAsyncThunk(
    'terms/update',
    async (data: { lastUpdated: string; sections: Section[] }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_TERMS_OF_SERVICE, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update terms of service');
        }
    }
);

export const addTermsSection = createAsyncThunk(
    'terms/addSection',
    async (data: { title: string; order?: number; subSections?: SubSection[] }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_TERMS_SECTION, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add section');
        }
    }
);

export const updateTermsSection = createAsyncThunk(
    'terms/updateSection',
    async ({ sectionId, data }: { sectionId: string; data: { title?: string; order?: number } }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_TERMS_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update section');
        }
    }
);

export const deleteTermsSection = createAsyncThunk(
    'terms/deleteSection',
    async (sectionId: string, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_TERMS_SECTION(sectionId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete section');
        }
    }
);

export const addTermsSubSection = createAsyncThunk(
    'terms/addSubSection',
    async ({ sectionId, data }: { sectionId: string; data: { title: string; content: string } }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_TERMS_SUB_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add sub-section');
        }
    }
);

export const updateTermsSubSection = createAsyncThunk(
    'terms/updateSubSection',
    async (
        { sectionId, subId, data }: { sectionId: string; subId: string; data: { title?: string; content?: string } },
        { rejectWithValue }
    ) => {
        try {
            const response = await put(UPDATE_TERMS_SUB_SECTION(sectionId, subId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update sub-section');
        }
    }
);

export const deleteTermsSubSection = createAsyncThunk(
    'terms/deleteSubSection',
    async ({ sectionId, subId }: { sectionId: string; subId: string }, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_TERMS_SUB_SECTION(sectionId, subId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete sub-section');
        }
    }
);

const termsSlice = createSlice({
    name: 'terms',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTermsOfService.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTermsOfService.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchTermsOfService.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createTermsOfService.pending, (state) => {
                state.sectionLoading = true;
                state.error = null;
            })
            .addCase(createTermsOfService.fulfilled, (state, action) => {
                state.sectionLoading = false;
                state.data = action.payload;
            })
            .addCase(createTermsOfService.rejected, (state, action) => {
                state.sectionLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updateTermsOfService.pending, (state) => {
                state.sectionLoading = true;
                state.error = null;
            })
            .addCase(updateTermsOfService.fulfilled, (state, action) => {
                state.sectionLoading = false;
                state.data = action.payload;
            })
            .addCase(updateTermsOfService.rejected, (state, action) => {
                state.sectionLoading = false;
                state.error = action.payload as string;
            })
            .addCase(addTermsSection.pending, (state) => {
                state.sectionLoading = true;
            })
            .addCase(addTermsSection.fulfilled, (state, action) => {
                state.sectionLoading = false;
                state.data = action.payload;
            })
            .addCase(addTermsSection.rejected, (state, action) => {
                state.sectionLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updateTermsSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deleteTermsSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(addTermsSubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updateTermsSubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deleteTermsSubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            });
    },
});

export const { clearError } = termsSlice.actions;
export default termsSlice.reducer;
